import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {checkoutState, topUpStatus} from 'utils/billing/billingApi';
import {planReturnMessage, topUpReturnMessage} from 'utils/billing/billingModel';
import {loadPlanInfo} from 'utils/billing/planInfo';
import {showWorldNotice} from './worldNotice';

const ATTEMPTS = 10;
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Back in the world from Stripe (Sol build-plan D5): say what happened, from the server - never from
 * the address - and refresh the plan so the Upgrade pill and limits are current.
 */
export default function WorldReturnNotice() {
  const dispatch = useDispatch();
  // The answer usually arrives while the world is still loading; it is held until the learner can see the
  // world - Quikbase as soon as it has loaded, a unit once it is ready to move (Quikbase never sends
  // ready-to-move) - and never shown or cleared before that (Sol audit-round-ship).
  const worldReady = useSelector(state => {
    if (state.world?.worldLoading !== false) return false;
    return state.world?.location === 'quikbase' || Boolean(state.meeting?.meeting?.ready_to_move);
  });
  const [message, setMessage] = useState(null);
  useEffect(() => {
    if (!message || !worldReady) return undefined;
    // The world loads in two phases and the loaded flag flickers between them; show the notice only once
    // it has stayed loaded for a few seconds (and the loading screen has faded).
    const timer = setTimeout(() => {
      showWorldNotice({title: message.title, description: message.description, tone: message.tone === 'good' ? 'good' : 'neutral'});
      setMessage(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [message, worldReady]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const url = new URL(window.location.href);
    const checkout = url.searchParams.get('checkout');
    const topup = url.searchParams.get('topup');
    const sessionId = url.searchParams.get('session_id');
    const purchaseId = url.searchParams.get('purchase');
    if (!checkout && !topup) return undefined;
    ['checkout', 'topup', 'purchase', 'session_id'].forEach(key => url.searchParams.delete(key));
    window.history.replaceState(window.history.state, '', url.toString());

    let cancelled = false;
    const show = next => {
      if (cancelled) return;
      setMessage(next);
      dispatch(loadPlanInfo());
    };
    // Asks until the server has an answer; a checkout that is not this learner's (404) says nothing.
    const follow = async (read, decide) => {
      for (let attempt = 1; attempt <= ATTEMPTS && !cancelled; attempt += 1) {
        let value = null;
        try {
          value = await read();
        } catch (error) {
          if (error?.response?.status === 404) return;
        }
        const message = decide(value, attempt === ATTEMPTS);
        if (message) return show(message);
        await wait(2000);
      }
    };

    if (checkout === 'cancel' || topup === 'cancel') {
      setMessage({title: 'Payment cancelled', description: 'Nothing was charged.', tone: 'neutral'});
    } else if (checkout === 'success' && sessionId) {
      follow(() => checkoutState(sessionId), planReturnMessage);
    } else if (topup === 'success' && purchaseId) {
      follow(() => topUpStatus(purchaseId), topUpReturnMessage);
    }
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  return null;
}
